import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, uploadFile, openDmsFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UploadProgress } from "@/components/common/UploadProgress";
import { DmsFileActions } from "@/components/common/DmsFileActions";
import { SearchInput } from "@/components/common/SearchInput";
import { relative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Folder, FileText, FileArchive, ChevronRight, Upload, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const ICON = { pdf: FileText, zip: FileArchive };

function UploadFileDialog({ folders, defaultFolderId, onUploaded }) {
  const { me } = useApp();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [folderId, setFolderId] = useState(defaultFolderId || "");
  const [name, setName] = useState("");
  const [progress, setProgress] = useState(null);

  const upload = useMutation({
    mutationFn: (fd) => uploadFile("/dms/files", fd, setProgress),
    onSuccess: () => { toast.success("File uploaded"); setOpen(false); setFile(null); setName(""); setProgress(null); onUploaded(); },
    onError: (e) => { setProgress(null); toast.error(e?.response?.data?.detail || "Could not upload file"); },
  });

  const submit = () => {
    if (!file || !folderId) { toast.error("File and folder are required"); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    fd.append("folder_id", folderId);
    fd.append("uploaded_by", me?.id || "");
    upload.mutate(fd);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="upload-file"><Upload className="mr-1.5 h-4 w-4" />Upload File</Button></DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="upload-file-modal">
        <DialogHeader><DialogTitle>Upload File</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>File *</Label>
            <input type="file" data-testid="field-file" className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div><Label>File Name (optional)</Label><Input data-testid="field-name" className="mt-1" placeholder={file?.name} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div>
            <Label>Folder *</Label>
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger className="mt-1" data-testid="field-folder_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <UploadProgress percent={progress} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={upload.isPending}>Cancel</Button>
          <Button data-testid="upload-file-modal-submit" onClick={submit} disabled={upload.isPending}>Upload</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DMS() {
  const { can } = useApp();
  const [folderId, setFolderId] = useState(null);
  const [folderSearch, setFolderSearch] = useState("");
  const [fileSearch, setFileSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const qc = useQueryClient();
  const { data: folders = [], isLoading } = useQuery({ queryKey: ["dms-folders"], queryFn: () => get("/dms/folders") });
  const { data: files = [] } = useQuery({ queryKey: ["dms-files", folderId], queryFn: () => get(`/dms/files${folderId ? `?folder_id=${folderId}` : ""}`), enabled: !!folderId });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const current = folders.find((f) => f.id === folderId);

  const filteredFolders = useMemo(() => folders.filter((f) => f.name.toLowerCase().includes(folderSearch.toLowerCase())), [folders, folderSearch]);
  const allTags = useMemo(() => [...new Set(files.flatMap((f) => f.tags || []))], [files]);
  const filteredFiles = useMemo(() => files.filter((f) =>
    (!fileSearch || f.name.toLowerCase().includes(fileSearch.toLowerCase())) &&
    (tagFilter === "all" || (f.tags || []).includes(tagFilter))
  ), [files, fileSearch, tagFilter]);

  const createFolder = useMutation({ mutationFn: (b) => post("/dms/folders", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["dms-folders"] }); toast.success("Folder created"); } });
  const onUploaded = () => { qc.invalidateQueries({ queryKey: ["dms-files", folderId] }); qc.invalidateQueries({ queryKey: ["dms-folders"] }); };

  return (
    <div>
      <PageHeader title="Documents"
        subtitle={<span className="flex items-center gap-1"><button onClick={() => setFolderId(null)} className="hover:underline">Home</button>{current && <><ChevronRight className="h-3 w-3" />{current.name}</>}</span>}>
        <FormDialog title="New Folder" testid="new-folder-modal"
          trigger={<Button variant="outline" data-testid="new-folder"><FolderPlus className="mr-1.5 h-4 w-4" />New Folder</Button>}
          fields={[{ name: "name", label: "Folder Name", required: true, full: true }]}
          onSubmit={(v) => createFolder.mutateAsync(v)} />
        <UploadFileDialog folders={folders} defaultFolderId={folderId} onUploaded={onUploaded} />
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : !folderId ? (
        <>
          <div className="mb-4"><SearchInput testid="folder-search" placeholder="Search folders" value={folderSearch} onChange={setFolderSearch} /></div>
          {!filteredFolders.length ? <EmptyState icon={Folder} title="No folders match" /> : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {filteredFolders.map((f) => (
                <button key={f.id} data-testid={`folder-${f.id}`} onClick={() => setFolderId(f.id)}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left hover:border-primary/40 hover:shadow-sm">
                  <Folder className="h-8 w-8 text-amber-500" />
                  <div><div className="text-sm font-medium">{f.name}</div><div className="text-xs text-muted-foreground">{f.file_count} files</div></div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <SearchInput testid="file-search" placeholder="Search files" value={fileSearch} onChange={setFileSearch} />
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-44" data-testid="tag-filter"><SelectValue placeholder="All tags" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All tags</SelectItem>{allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {files.length === 0 ? <EmptyState icon={FileText} title="No files in this folder" /> : !filteredFiles.length ? (
            <EmptyState icon={FileText} title="No files match" />
          ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Tags</th>
              <th className="px-4 py-3 font-semibold">Size</th><th className="px-4 py-3 font-semibold">Uploaded By</th>
              <th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {filteredFiles.map((f) => {
                const Icon = ICON[f.type] || FileText;
                return (
                  <tr key={f.id} data-testid={`file-${f.id}`} onClick={() => openDmsFile(f.id)} className="group cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3"><div className="flex items-center gap-2 font-medium"><Icon className="h-4 w-4 text-muted-foreground" />{f.name}</div></td>
                    <td className="px-4 py-3"><div className="flex gap-1">{f.tags.map((t) => <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t}</span>)}</div></td>
                    <td className="px-4 py-3 text-muted-foreground">{f.size}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Avatar user={umap[f.uploaded_by]} size="xs" /><span className="text-xs">{umap[f.uploaded_by]?.name}</span></div></td>
                    <td className="px-4 py-3 text-muted-foreground">{relative(f.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <DmsFileActions file={f} queryKey={["dms-files", folderId]} canConvert={can("dms", "edit")} canDelete={can("dms", "delete")} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}
    </div>
  );
}
